import { roomEvents } from "@closerplatform/closer-sdk";
import { HistoryFilter } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import { MeetingSchedulerStatus } from "@fidget/common/models/meeting-scheduler-status";
import { flow, Instance, SnapshotIn, types } from "mobx-state-tree";
import { markLastMessage } from "@fidget/common/closer";
import {
  getLanguageOption,
  LanguageOption
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { IAttachment } from "@fidget/common/components/mstchat/chatMessage";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { MessageTag } from "@fidget/common/models/messageTag";
import { logger } from "@fidget/common/utils";
import { RoomListener } from "@fidget/common/services/types";
import { AgentReferenceType, IAgentReferenceType } from "../agent/types";
import { Conversation } from "../base/Conversation";
import {
  currentRoomGetter,
  currentUserStoreGetter,
  guestStoreGetter,
  servicesGetters,
  topicStoreGetter,
  uiStoreGetter
} from "../utils";
import { artichokeEventsRoomListener } from "./actions/artichokeEventsRoomListener";
import { Chat, ChatItemAssertion } from "./chat";
import { ChatItem, IChatItem } from "./chat/ChatItem";
import { ChatInput, InputType } from "./ChatInput";
import { roomEventToChatItemSnapshot } from "./utils";
import { ConversationStatus } from "@closerplatform/spinner-openapi";
import { RoomSummaryContext } from "@fidget/common/models/chat/roomSummaryContext";
import { uniq } from "lodash";

const CHAT_PAGE_SIZE = 30; // chat-page side: 2-3 pages on FHD screen

export type Meeting = { id: string; start: number; duration: number; agent: string };

const RoomMeeting = types.model("RoomMeeting", {
  id: types.identifier,
  start: types.number,
  duration: types.number,
  agent: AgentReferenceType
});

const CoreRoom = types
  .compose(
    "Room",
    Conversation,
    types.model({
      queryForResolve: false,
      meetings: types.array(RoomMeeting),
      chatInput: types.optional(ChatInput, () =>
        ChatInput.create({ activeInputType: InputType.Message, fileStore: {} })
      ),
      chat: types.optional(Chat, () => Chat.create())
    })
  )
  .views(servicesGetters)
  .views(currentUserStoreGetter)
  .views(currentRoomGetter)
  .views(guestStoreGetter)
  .views(topicStoreGetter)
  .views(uiStoreGetter)
  .views((self) => {
    const views = {
      get isFollowedByMe(): boolean {
        const currentUserId = self.currentUserProfile && self.currentUserProfile.id;
        return !!self.followers.find((f: IAgentReferenceType) => f.id === currentUserId);
      },
      get isSelected(): boolean {
        const currentRoom = self.currentRoomReference;
        return currentRoom !== undefined && currentRoom === self;
      },
      get meetingSchedulingInProgress(): boolean {
        return !!self.chat.chatItems.some(
          (item: IChatItem) =>
            ChatItemAssertion.isMeetingScheduler(item) &&
            item.progressStatus === MeetingSchedulerStatus.PENDING
        );
      },
      get languageOption(): LanguageOption {
        return (
          getLanguageOption(self.owner.languageLocale) || self.currentUserStore.currentLanguage
        );
      },
      get isLastMessageFromAdviser(): boolean {
        return (
          self.lastAdviserTimestamp !== undefined &&
          self.lastAdviserTimestamp > self.lastGuestTimestamp
        );
      },
      get isChatFocusedOnMostRecent(): boolean {
        return (
          self.userActivityService.isPageActive() &&
          views.isSelected &&
          self.chat.chatScrolledToBottom
        );
      }
    };

    return views;
  })
  .actions((self) => ({
    /**
     * Handle successful loading of chat items page
     * TODO: move to Chat model
     */
    chatPageLoaded: flow(function* (page: ReadonlyArray<roomEvents.RoomEvent>) {
      const summariesRoomIds = page
        .filter(
          (e) =>
            roomEvents.CustomMessageSent.isCustomMessageSent(e) &&
            e.subtag === MessageTag.ROOM_SUMMARY
        )
        .map((e) => ((e as roomEvents.CustomMessageSent).context as RoomSummaryContext).roomId);
      yield Promise.all(
        uniq(summariesRoomIds).map((roomId) => self.guestStore.loadByRoomId(roomId))
      );

      self.chat.oldestTimestamp = page[0]?.timestamp;
      self.chat.historyIsFullyLoaded = page.length < CHAT_PAGE_SIZE;
      self.chat.hasFake = page.find((e) => e.tag === "fake_room_message_sent") !== undefined;

      const items: SnapshotIn<typeof ChatItem> = yield Promise.all(
        page.map((item) => roomEventToChatItemSnapshot(item, self.spinnerService))
      );
      self.chat.isLoading = false;

      self.chat.addChatItems(items);

      const mark = self.chat.marks.get(self.owner.id);
      if (mark) {
        self.chat.setReadUntilMark(mark.user, mark.mark);
      }
    }),

    chatPageReloaded: flow(function* (page: ReadonlyArray<roomEvents.RoomEvent>) {
      self.chat.oldestTimestamp = page[0]?.timestamp;
      self.chat.historyIsFullyLoaded = page.length < CHAT_PAGE_SIZE;
      self.chat.hasFake = page.find((e) => e.tag === "fake_room_message_sent") !== undefined;

      const items: SnapshotIn<typeof ChatItem> = yield Promise.all(
        page.map((item) => roomEventToChatItemSnapshot(item, self.spinnerService))
      );
      self.chat.isLoading = false;
      self.chat.reloadChatItems(items);

      const mark = self.chat.marks.get(self.owner.id);
      if (mark) {
        self.chat.setReadUntilMark(mark.user, mark.mark);
      }
    })
  }))
  .extend((self) => {
    const historyFilter: HistoryFilter = {
      // TODO: also asking for Intent for all rooms
      customFilter: Object.values(MessageTag),
      filter: [roomEvents.MessageSent.tag]
    };

    const views = {
      /**
       * Check if local chat history is up to date with remote message store
       * @returns {boolean}
       */
      hasLatestMessage: flow(function* () {
        const { chatEntries } = self.chat;
        const lastKeptMessageTimestamp = chatEntries.slice(-1)[0]?.timestamp;

        const lastPage = yield self.artichokeService.latestMessages(self.id, 1, historyFilter);
        const lastMessageTimestamp = lastPage[0]?.timestamp;

        return lastMessageTimestamp === lastKeptMessageTimestamp;
      })
    };

    const actions = {
      /**
       * Perform initial loading of chat items
       * Can be called as part of refresh-after-reconnect.
       * TODO: move to Chat model
       */
      loadChat: flow(function* () {
        // make sure messages are up to date
        if (self.chat.oldestTimestamp !== undefined) {
          const hasLatest = yield views.hasLatestMessage();
          if (!hasLatest) {
            self.chat.oldestTimestamp = undefined;
            self.chat.historyIsFullyLoaded = false;
          }
        }
        if (self.chat.oldestTimestamp === undefined) {
          const page: ReadonlyArray<roomEvents.RoomEvent> = yield self.artichokeService.latestMessages(
            self.id,
            CHAT_PAGE_SIZE,
            historyFilter
          );
          yield self.chatPageLoaded(page);
        }
        // FIXME: the call is using grouped entries - is this ok?
        markLastMessage(
          { artichokeService: self.artichokeService },
          self.id,
          self.chat.chatEntries
        );
      }),

      reloadChat: flow(function* () {
        // make sure messages are up to date
        self.chat.oldestTimestamp = undefined;
        self.chat.historyIsFullyLoaded = false;
        const page: ReadonlyArray<roomEvents.RoomEvent> = yield self.artichokeService.latestMessages(
          self.id,
          CHAT_PAGE_SIZE,
          historyFilter
        );
        self.chatPageReloaded(page);
        // FIXME: the call is using grouped entries - is this ok?
        markLastMessage(
          { artichokeService: self.artichokeService },
          self.id,
          self.chat.chatEntries
        );
      }),

      /**
       * Handle UI event where chat is scrolled to the top of currently loaded items
       * TODO: move to Chat model
       */
      chatTopReached: flow(function* () {
        if (self.chat.oldestTimestamp && !self.chat.historyIsFullyLoaded) {
          self.chat.isLoading = true;
          try {
            const page: ReadonlyArray<roomEvents.RoomEvent> = yield self.artichokeService.pagedMessages(
              self.id,
              CHAT_PAGE_SIZE,
              self.chat.oldestTimestamp,
              { customFilter: Object.values(MessageTag), filter: [roomEvents.MessageSent.tag] }
            );
            yield self.chatPageLoaded(page);
          } catch (error) {
            logger.error({ message: "Chat top reached failed", error, roomId: self.id });
            throw new Error(error as string);
          }
        }
      }),

      /**
       * Handle UI event where chat is scrolled to the bottom of currently loaded items
       * TODO: move to Chat model
       */
      chatIsScrolledToBottom(scrolledToBottom: boolean) {
        if (
          scrolledToBottom &&
          self.userActivityService.isPageActive() &&
          (self.isAssignedToMe || self.isFollowedByMe)
        ) {
          actions.markAllMessages();
        }
        self.chat.chatScrolledToBottom = scrolledToBottom;
      },

      /**
       * Mark all loaded messages as read
       * FIXME: the call is using grouped entries - is this ok?
       * TODO: move to Chat model
       */
      markAllMessages() {
        markLastMessage(
          { artichokeService: self.artichokeService },
          self.id,
          self.chat.chatEntries
        );
      }
    };

    return { views, actions };
  })
  ///////////////////////////////////////////////////////////
  // basic operations on properties: (create, delete, update)
  ///////////////////////////////////////////////////////////
  .actions((self) => ({
    addMeeting(meeting: Meeting) {
      self.meetings.push(meeting);
    },
    removeMeeting(id: string) {
      const meeting = self.meetings.find((m) => m.id === id);
      if (meeting) {
        self.meetings.remove(meeting);
      }
    },
    rescheduleMeeting({ id, start, duration }: { id: string; start: number; duration: number }) {
      const meeting = self.meetings.find((m) => m.id === id);
      if (meeting) {
        meeting.start = start;
        meeting.duration = duration;
      }
    },
    /**
     * Send available meeting slots to client in order to schedule a meeting
     */
    startMeetingScheduling: flow(function* () {
      try {
        yield self.spinnerService.sendMeetingLure(self.roomId);
      } catch (error) {
        logger.error({ message: "Start meeting scheduling failed", error, roomId: self.id });
        throw new Error("Could not send meetings slots to client");
      }
    }),
    /**
     * Cancel meeting scheduling process identified by token
     * @param {string}
     */
    cancelMeetingScheduling: flow(function* () {
      try {
        yield self.spinnerService.recallMeetingLure(self.roomId);
      } catch (error) {
        logger.error({ message: "Cancel meeting scheduling failed", error, roomId: self.id });
        throw new Error("Could not cancel meeting scheduling process");
      }
    })
  }))
  ///////////////////////////////
  // backend operations.
  ///////////////////////////////
  .actions((self) => ({
    sendMessage: flow(function* (text: string, attachments?: ReadonlyArray<IAttachment>) {
      const service = self.artichokeService;
      const pending = self.chat.createPendingMessage(text, attachments);
      self.chatInput.clearMessageInput();
      try {
        if (attachments && attachments.length) {
          yield service.sendCustom(self.id, "", MessageTag.MESSAGE_WITH_ATTACHMENTS, {
            message: text,
            attachments
          });
        } else {
          yield service.send(self.id, text);
        }
        self.chat.pendingMessageSent(pending.id);
      } catch (e) {
        self.chat.pendingMessageFailed(pending.id);
      }
    }),
    sendNote: flow(function* (message: string) {
      try {
        yield self.artichokeService.sendCustom(self.id, "", MessageTag.NOTE, {
          body: message,
          user: self.currentUserProfile && self.currentUserProfile.id
        });
        self.chatInput.clearNoteInput();
      } catch (error) {
        logger.error({ message: "Couldn't send note", error, roomId: self.id });
        throw new Error("Could not send the note");
      }
    }),
    follow: flow(function* () {
      try {
        yield self.spinnerService.follow(self.id);
      } catch (error) {
        logger.error({ message: "Couldn't follow room", error, roomId: self.id });
        throw new Error(`Could not start following the room with id '${self.id}'`);
      }
    }),
    unfollow: flow(function* () {
      try {
        yield self.spinnerService.unfollow(self.id);
        self.queryForResolve = false;
      } catch (error) {
        logger.error({ message: "Couldn't unfollow room", error, roomId: self.id });
        throw new Error(`Could not stop following the room with id '${self.id}'`);
      }
    }),
    reopen: flow(function* () {
      self.queryForResolve = false;
      try {
        yield self.spinnerService.setConversationStatus(
          self.id,
          self.assignee ? ConversationStatus.InProgress : ConversationStatus.Waiting
        );
      } catch (error) {
        logger.error({ message: "Couldn't reopen room", error, roomId: self.id });
        throw new Error(`Could reopen the room with id '${self.id}'`);
      }
    }),
    close: flow(function* (resolved: boolean) {
      if (
        self.owner.tagGroupId &&
        self.topicStore.getTagGroupTopics(self.owner.tagGroupId).length > 0 &&
        self.owner.topics.length === 0
      ) {
        self.uiStore.setChatNotification({
          message: intl.formatMessage("chat.notification.topicEmpty")
        });
        return;
      }

      self.queryForResolve = false;
      try {
        yield self.spinnerService.setConversationStatus(
          self.id,
          resolved ? ConversationStatus.Solved : ConversationStatus.Unsolved
        );
      } catch (error) {
        logger.error({ message: "Couldn't close room", error, roomId: self.roomId });
        throw new Error(`Could close the room with id '${self.id}'`);
      }
    }),
    assignAgent: flow(function* (id: string) {
      try {
        yield self.spinnerService.setAssignee(self.id, id);
      } catch (error) {
        logger.error({ message: "Couldn't assign room agent", error, roomId: self.id });
        throw new Error(`Could not assign agent to the room with id '${self.id}'`);
      }
    }),
    addNewFollower: flow(function* (userId: string) {
      try {
        yield self.spinnerService.addFollower(self.id, { userId });
      } catch (error) {
        logger.error({ message: "Couldn't add room follower", error, roomId: self.id });
        throw new Error(`Could not add follower '${userId}' to the room with id '${self.id}'`);
      }
    })
  }))
  // backend actions that depends on others
  .actions((self) => ({
    sendContactEnquiry: flow(function* () {
      const { orgConfig } = self.currentUserStore;
      const enquiryTag = orgConfig?.agreementEnabled
        ? MessageTag.CONTACT_ENQUIRY
        : MessageTag.CONTACT_ENQUIRY_WITHOUT_GDPR;
      const context = orgConfig?.agreementEnabled ? { agreement: orgConfig.agreement } : {};
      yield self.sendMessage(intl.formatMessage("richMessages.infoCapture.message"));
      yield self.artichokeService.sendCustom(self.id, "", enquiryTag, context);
    }),
    sendCallProposal: flow(function* () {
      const message = intl.formatMessage("richMessages.callProposal.message", {
        user: self.currentUserStore.profile?.firstName
      });
      yield self.artichokeService.sendCustom(self.id, message, MessageTag.CALL_PROPOSAL, {});
    }),
    sendCobrowsingProposal: flow(function* () {
      const message = intl.formatMessage("richMessages.cobrowsingProposal.message", {
        user: self.currentUserStore.profile?.firstName
      });
      yield self.artichokeService.sendCustom(self.id, message, MessageTag.COBROWSING_PROPOSAL, {});
    }),
    sendFileDownloaded: flow(function* (fileUrl: string, fileId?: string, fileName?: string) {
      yield self.artichokeService.sendCustom(self.id, "", MessageTag.FILE_DOWNLOADED, {
        fileUrl,
        fileName,
        fileId
      });
    }),
    sendLinkClicked: flow(function* (link: string) {
      yield self.artichokeService.sendCustom(self.id, "", MessageTag.LINK_CLICKED, { link });
    })
  }))
  /////////////////////
  // Initialize room internals...
  ////////////////////
  .actions((self) => {
    const actions = {
      loadGuestProfile: flow(function* () {
        const profile = yield self.spinnerService.getGuestProfileByRoom(self.id);
        self.owner.update(profile);
      }),

      loadMeetings: flow(function* () {
        try {
          const meetings: Array<Meeting> = yield self.spinnerService.getRoomMeetings(self.id);
          self.meetings.replace(meetings.map((m) => Object.assign({}, { ...m }) as any));
        } catch (error) {
          logger.error({ message: "Couldn't load room meetings", error, roomId: self.id });
          throw new Error("Could not load room meetings");
        }
      })
    };
    return actions;
  });

export const Room = CoreRoom.actions(artichokeEventsRoomListener).actions((self) => ({
  prepare() {
    self.loadChat();
    self.loadGuestProfile();
    self.loadMeetings();
    self.eventListener.addRoomListener(self.id, self as RoomListener);
  }
}));

export interface IRoomMeeting extends Instance<typeof RoomMeeting> { }
export interface ICoreRoom extends Instance<typeof CoreRoom> { }
export interface IRoom extends Instance<typeof Room> { }
